import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormInputTextProps } from 'features/common/components/Form/types';
import { InputLabel } from 'features/common/components/Form/InputLabel';
import { Error } from 'features/common/components/Form/Form.styles';

import { PunchupFieldLabel } from 'features/common/components/Punchup/primitives/forms/fields/PunchupFieldLabel';

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { InputContainer, StyledQuillWrapper } from './ShowDescriptionInput.styles';

/**
 * Rich-text editor for a show/event description (PUN-5353). A react-quill editor
 * CONSTRAINED to the body-HTML vocabulary Punchup's renderer keeps
 * (@punchup/utils/rich-text sanitizeBodyHtml) so a description written here
 * round-trips cleanly through the Punchup show page and the checkout widget:
 *
 *   headers h2/h3/h4 (NOT h1 — the renderer strips it) · bold · italic ·
 *   underline · blockquote · color · ordered/bullet lists · link
 *
 * Deliberately DROPPED vs the email RichTextInput, because the Punchup renderer
 * strips them (they would silently vanish on the public page):
 *   - strike (<s>) and background color — not in the inline vocabulary
 *   - alignment — Quill emits class-based align (`ql-align-*`); the renderer
 *     reads style-based `text-align`, so a class-aligned block loses its
 *     alignment. Omitting the tool avoids the silent loss.
 *
 * Unlike the email editor this has no enable/disable toggle — a description is
 * always a single always-on field. Binds to react-hook-form by `name`.
 */
export const ShowDescriptionInput = ({
	id,
	name,
	label,
	isPunchup = false,
}: FormInputTextProps & { isPunchup?: boolean }) => {
	const {
		setValue,
		watch,
		formState: { errors },
	} = useFormContext();

	const error = errors[name as keyof typeof errors];
	const errorMessage = (error as { message?: string } | undefined)?.message;
	const value: string = watch(name as string) || '';

	const modules = useMemo(
		() => ({
			toolbar: [
				[{ header: [2, 3, 4, false] }],
				['bold', 'italic', 'underline', 'blockquote'],
				[{ color: [] }],
				[{ list: 'ordered' }, { list: 'bullet' }],
				['link', 'clean'],
			],
			clipboard: { matchVisual: false },
		}),
		[]
	);

	const formats = useMemo(
		() => ['header', 'bold', 'italic', 'underline', 'blockquote', 'color', 'list', 'bullet', 'link'],
		[]
	);

	return (
		<InputContainer hasError={Boolean(error)} isPunchup={isPunchup}>
			{label &&
				(isPunchup ? (
					<PunchupFieldLabel htmlFor={id ?? name} label={label} />
				) : (
					<InputLabel htmlFor={id ?? name} label={label} />
				))}
			<StyledQuillWrapper isPunchup={isPunchup}>
				<ReactQuill
					id={id ?? name}
					theme="snow"
					value={value}
					modules={modules}
					formats={formats}
					onChange={(html) => setValue(name as string, html, { shouldDirty: true })}
				/>
			</StyledQuillWrapper>
			{errorMessage &&
				(isPunchup ? (
					// Punchup mode uses the token-CSS error role; the themed <Error>
					// (styled(Text)) needs a ThemeProvider the punchup surfaces don't give.
					<span className="punchupFormError">{errorMessage}</span>
				) : (
					<Error>{errorMessage}</Error>
				))}
		</InputContainer>
	);
};
